import type { SidebarMenuItem } from "@shared/types/sidebarMenu";
import type { UserMenuItem } from "@shared/types/user";
import { APP_DASHBOARD_PATH, APP_LAYOUT_ROUTE_NAME, APP_LAYOUT_ROUTE_PATH, MENU_HIDDEN, MENU_TYPE_BUTTON, MENU_TYPE_GROUP, MENU_TYPE_MENU } from "@shared/constants/menu";
import { APP_TARGET } from "@shared/utils/storage";
import { resolveMenuPage, stripAppPrefix } from "./routePageExtras";
import type { RouteComponent, RouteRecordRaw } from "vue-router";

function isCustomerWorkbench(): boolean {
    return APP_TARGET === "customer";
}

/** 客户端 /dashboard 是真实工作台；其它端仍只作登录桥接 */
function dashboardPageMeta(): { hidden: boolean; hideInMenu: boolean; affix: boolean; keepAliveName: string } {
    if (isCustomerWorkbench()) {
        return { hidden: false, hideInMenu: true, affix: true, keepAliveName: "Dashboard" };
    }
    return { hidden: true, hideInMenu: true, affix: false, keepAliveName: "Dashboard" };
}

export interface SplitUserMenusResult {
    /** 给侧栏：已去掉 hidden 节点；分组可嵌套。不依赖页面表，避免没挂页的模块整组消失 */
    sidebar: SidebarMenuItem[];
    /** 给 Vue Router：只含能打开的叶子（含 hidden，方便深链） */
    pages: RouteRecordRaw[];
    /** type=2 且有 path 的节点，供 v-permission */
    buttonPaths: string[];
}

/**
 * 菜单 path 历史 kebab → 现行 camelCase。
 * 用于旧页签 / 书签跳转，避免改库 route 后访问旧 URL 落到 404。
 */
export const LEGACY_MENU_PATH_REDIRECTS: Readonly<Record<string, string>> = {
    "/store/amazon-vc": "/store/amazonVc",
    "/store/amazon-ad-account": "/store/amazonAd",
    "/store/walmart-dsv": "/store/walmartDsv",
};

/**
 * 把 GET /menus 的 items 拆成侧栏树 + 叶子路由 + 按钮 path。
 * 侧栏按接口树展示；页面表只决定能不能打开页面，不再决定侧栏出不出。
 */
export function splitUserMenus(menus: UserMenuItem[]): SplitUserMenusResult {
    const usedNames = new Set<string>();
    const usedPaths = new Set<string>();
    const usedRegistryKeys = new Set<string>();
    const buttonPaths = new Set<string>();
    const pages: RouteRecordRaw[] = [];

    const sidebar = mapSidebarNodes(menus ?? [], pages, usedNames, usedPaths, usedRegistryKeys, buttonPaths);
    ensureDashboardPage(pages, usedNames, usedPaths, usedRegistryKeys);
    ensurePersonalCenterPage(pages, usedNames, usedPaths, usedRegistryKeys);
    ensureLegacyMenuPathRedirects(pages, usedNames, usedPaths);
    return { sidebar, pages, buttonPaths: [...buttonPaths] };
}

function menuItems(menu: UserMenuItem): UserMenuItem[] {
    return menu.items ?? [];
}

function menuTypeOf(menu: UserMenuItem): number {
    return Number(menu.type);
}

function isMenuHidden(menu: UserMenuItem): boolean {
    return Number(menu.hidden) === MENU_HIDDEN;
}

/** /dashboard 只作登录桥接，不进侧栏。菜单 path 带 /manage 等前缀时同样认。 */
function isDashboardPath(path: string): boolean {
    return stripAppPrefix(path) === APP_DASHBOARD_PATH;
}

/** /dashboard 只作登录桥接，不进侧栏 */
function isBridgeHomeMenu(path: string): boolean {
    return isDashboardPath(path);
}

/** 分组，或历史数据里 type=菜单但下面还挂了菜单/分组（旧 mapOne 会当目录渲染） */
function isSidebarBranch(menu: UserMenuItem): boolean {
    if (menuTypeOf(menu) === MENU_TYPE_GROUP) return true;
    return menuItems(menu).some(child => {
        const type = menuTypeOf(child);
        return type === MENU_TYPE_GROUP || type === MENU_TYPE_MENU;
    });
}

function mapSidebarNodes(menus: UserMenuItem[], pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>, buttonPaths: Set<string>): SidebarMenuItem[] {
    const out: SidebarMenuItem[] = [];
    for (const menu of menus) {
        if (menuTypeOf(menu) === MENU_TYPE_BUTTON) {
            registerButton(menu, pages, usedNames, usedPaths, usedRegistryKeys, buttonPaths);
            continue;
        }

        if (isSidebarBranch(menu)) {
            const children = mapSidebarNodes(menuItems(menu), pages, usedNames, usedPaths, usedRegistryKeys, buttonPaths);
            if (isMenuHidden(menu)) continue;
            if (!children.length) continue;
            out.push({
                id: String(menu.id),
                title: menu.title || "",
                type: "group",
                icon: menu.icon || undefined,
                selectIcon: menu.selectIcon || undefined,
                children,
            });
            continue;
        }

        if (menuTypeOf(menu) !== MENU_TYPE_MENU) continue;

        const page = tryBuildPage(menu, usedNames, usedPaths, usedRegistryKeys);
        if (page) pages.push(page);
        for (const child of menuItems(menu)) {
            if (menuTypeOf(child) === MENU_TYPE_BUTTON) {
                registerButton(child, pages, usedNames, usedPaths, usedRegistryKeys, buttonPaths);
            }
        }

        const path = page?.path || toUrlPath(menu.path || "");
        if (isMenuHidden(menu) || isBridgeHomeMenu(path)) continue;
        if (!path) continue;

        out.push({
            id: String(menu.id),
            title: menu.title || page?.meta?.title || "",
            type: "page",
            path,
            icon: menu.icon || undefined,
            selectIcon: menu.selectIcon || undefined,
        });
    }
    return out;
}

function registerButton(menu: UserMenuItem, pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>, buttonPaths: Set<string>): void {
    const path = toUrlPath(menu.path || "");
    if (path) buttonPaths.add(path);
    const page = tryBuildButtonPage(menu, usedNames, usedPaths, usedRegistryKeys);
    if (page) pages.push(page);
    for (const child of menuItems(menu)) {
        if (menuTypeOf(child) === MENU_TYPE_BUTTON) {
            registerButton(child, pages, usedNames, usedPaths, usedRegistryKeys, buttonPaths);
        }
    }
}

function tryBuildPage(menu: UserMenuItem, usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): RouteRecordRaw | null {
    const resolved = resolveMenuPage(menu.path || "");
    if (!resolved) {
        console.warn(`[routePages] 未匹配到页面，已跳过: ${menu.title || menu.id} (${menu.path || ""})`);
        return null;
    }
    const path = resolved.routerPath;
    if (usedPaths.has(path)) {
        console.warn(`[routePages] 路由 path 重复，已跳过: ${menu.title || menu.id} (${path})`);
        return null;
    }

    const extra = resolved;
    usedRegistryKeys.add(toRouteName("", path, menu.id));

    const name = allocRouteName(extra.keepAliveName, path, menu.id, usedNames);
    usedPaths.add(path);

    return {
        path,
        name,
        component: extra.component,
        meta: {
            title: menu.title || undefined,
            menuType: MENU_TYPE_MENU,
            iconDefault: menu.icon || undefined,
            iconActive: menu.selectIcon || undefined,
            hidden: isMenuHidden(menu) || (isDashboardPath(path) && !isCustomerWorkbench()),
            noCache: menu.keepalive === 0,
            ...(isDashboardPath(path) ? dashboardPageMeta() : {}),
            ...(extra.noPin ? { noPin: true } : {}),
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    };
}

function tryBuildButtonPage(menu: UserMenuItem, usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): RouteRecordRaw | null {
    const resolved = resolveMenuPage(menu.path || "");
    if (!resolved || usedPaths.has(resolved.routerPath)) return null;
    const path = resolved.routerPath;
    const extra = resolved;
    usedRegistryKeys.add(toRouteName("", path, menu.id));

    const name = allocRouteName(extra.keepAliveName, path, menu.id, usedNames);
    usedPaths.add(path);

    return {
        path,
        name,
        component: extra.component,
        meta: {
            title: menu.title || undefined,
            menuType: MENU_TYPE_BUTTON,
            hidden: isMenuHidden(menu),
            hideInMenu: true,
            noCache: menu.keepalive === 0,
            permissionCode: path,
            ...(extra.noPin ? { noPin: true } : {}),
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    };
}

function ensureDashboardPage(pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): void {
    if (usedPaths.has(APP_DASHBOARD_PATH)) {
        const existing = pages.find(p => p.path === APP_DASHBOARD_PATH);
        if (existing) {
            existing.meta = {
                ...(existing.meta || {}),
                ...dashboardPageMeta(),
                title: existing.meta?.title || "首页",
            };
        }
        return;
    }
    const extra = resolveMenuPage("/dashboard");
    if (!extra?.component) return;
    usedPaths.add(APP_DASHBOARD_PATH);
    usedNames.add("Dashboard");
    usedRegistryKeys.add("Dashboard");
    pages.unshift({
        path: APP_DASHBOARD_PATH,
        name: "Dashboard",
        component: extra.component,
        meta: {
            title: "首页",
            ...dashboardPageMeta(),
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    });
}

function ensurePersonalCenterPage(pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>, usedRegistryKeys: Set<string>): void {
    if (usedPaths.has("/system/personalCenter")) return;
    const extra = resolveMenuPage("/system/personalCenter");
    if (!extra?.component) return;
    usedPaths.add("/system/personalCenter");
    usedRegistryKeys.add("SystemPersonalCenter");
    const name = allocRouteName("SystemPersonalCenter", "/system/personalCenter", "personalCenter", usedNames);
    pages.push({
        path: "/system/personalCenter",
        name,
        component: extra.component,
        meta: {
            title: "个人中心",
            hideInMenu: true,
            ...(extra.keepAliveName ? { keepAliveName: extra.keepAliveName } : {}),
        },
    });
}

/** 把已知旧 path 注册为隐藏 redirect，仅当新 path 已在菜单树中 */
function ensureLegacyMenuPathRedirects(pages: RouteRecordRaw[], usedNames: Set<string>, usedPaths: Set<string>): void {
    for (const [from, to] of Object.entries(LEGACY_MENU_PATH_REDIRECTS)) {
        if (!usedPaths.has(to) || usedPaths.has(from)) continue;
        const name = allocRouteName(`LegacyRedirect${from.replace(/\W+/g, "")}`, from, from, usedNames);
        usedPaths.add(from);
        pages.push({
            path: from,
            name,
            redirect: to,
            meta: { hidden: true, hideInMenu: true },
        });
    }
}

/** 组件 path → 路由 URL：去 .vue、去尾部 /index、补前导 / */
export function toUrlPath(raw: string): string {
    let p = String(raw || "")
        .trim()
        .replace(/\\/g, "/")
        .replace(/\.vue$/i, "");
    if (!p) return "";
    p = p.replace(/\/index$/i, "");
    if (!p.startsWith("/")) p = `/${p}`;
    p = p.replace(/\/+/g, "/");
    if (p.length > 1 && p.endsWith("/")) p = p.slice(0, -1);
    return p;
}

/**
 * 分配唯一 route name：由 path 推导，撞名时加序号，避免 keep-alive 串缓存。
 */
function allocRouteName(rawName: string | undefined, path: string, id: number | string, usedNames: Set<string>): string {
    const preferred = toRouteName(rawName, path, id);
    const fromPath = toRouteName("", path, id);
    const candidates = preferred === fromPath ? [preferred] : [preferred, fromPath];
    for (const candidate of candidates) {
        if (candidate && !usedNames.has(candidate)) {
            usedNames.add(candidate);
            return candidate;
        }
    }
    const base = fromPath || preferred || `Menu${id}`;
    let i = 2;
    while (usedNames.has(`${base}${i}`)) i += 1;
    const unique = `${base}${i}`;
    usedNames.add(unique);
    return unique;
}

/** Vue Router name 不能含 /；由 path 生成 PascalCase */
export function toRouteName(rawName: string | undefined, path: string, id: number | string): string {
    const name = String(rawName || "").trim();
    if (name && !name.includes("/")) return name;

    const fromPath = path
        .replace(/^\//, "")
        .split("/")
        .filter(Boolean)
        .map(seg =>
            seg
                .split("-")
                .filter(Boolean)
                .map(s => s.charAt(0).toUpperCase() + s.slice(1))
                .join(""),
        )
        .join("");

    return fromPath || `Menu${id}`;
}

/** `/product/goods` → `product/goods`，作为 Layout(`/`) 的相对子路由，浏览器 URL 仍是 /product/goods */
function toLayoutChildPath(path: string): string {
    const p = String(path || "").trim();
    if (!p || p === "/") return "";
    return p.startsWith("/") ? p.slice(1) : p;
}

/**
 * 所有业务页挂到同一 Layout。
 * 子路由必须用相对 path：父级若写 /__layout 且子级写绝对 /dashboard，登录后 replace('/dashboard')
 * 对不上叶子，会 404 / 导航失败，表现成「登录不上」。
 */
export function buildLayoutRoute(pages: RouteRecordRaw[], layout: RouteComponent): RouteRecordRaw {
    return {
        path: APP_LAYOUT_ROUTE_PATH,
        name: APP_LAYOUT_ROUTE_NAME,
        component: layout,
        meta: { hidden: true },
        children: pages.map(page => ({
            ...page,
            path: toLayoutChildPath(String(page.path || "")),
        })),
    };
}
